import React, { useState, useEffect } from 'react'
import { Plus, X, ChevronDown, Star } from 'lucide-react'
import { apiRequest, getApiHeaders, buildApiUrl } from '@/config/api'

interface Language {
  id: number
  language_name: string
  category: string
  description?: string
}

interface UserLanguage {
  language_id: number
  language_name: string
  speaking_rating: number
  reading_rating: number
  writing_rating: number
}

interface LanguageSelectorProps {
  value: UserLanguage[]
  onChange: (languages: UserLanguage[]) => void
  disabled?: boolean
}

// Removed proficiency levels as we're using separate ratings

const RATING_LABELS = {
  1: 'Poor',
  2: 'Fair', 
  3: 'Good',
  4: 'Very Good',
  5: 'Excellent'
}

export default function LanguageSelector({ value = [], onChange, disabled = false }: LanguageSelectorProps) {
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newLanguage, setNewLanguage] = useState<Partial<UserLanguage>>({
    language_id: 0,
    language_name: '',
    speaking_rating: 3,
    reading_rating: 3,
    writing_rating: 3
  })

  useEffect(() => {
    loadLanguages()
  }, [])

  const loadLanguages = async () => {
    try {
      setIsLoading(true)
      
      // Use consistent API pattern
      const headers = getApiHeaders(true)
      const url = buildApiUrl('languages/list?category=Spoken')
      
      console.log('🔍 Loading languages from:', url) // Debug log
      
      const response = await fetch(url, { headers })
      const result = await response.json()
      
      console.log('🔍 Languages API response:', result) // Debug log
      
      if (result.success && result.data) {
        setAvailableLanguages(result.data)
        console.log('✅ Loaded languages:', result.data.length) // Debug log
      } else {
        console.error('❌ Failed to load languages:', result)
      }
    } catch (error) {
      console.error('❌ Failed to load languages:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const availableLanguagesForDropdown = availableLanguages.filter(lang => 
    !value.some(userLang => userLang.language_id === lang.id)
  )

  const handleAddLanguage = () => {
    if (newLanguage.language_id && newLanguage.language_name) {
      const updatedLanguages = [...value, newLanguage as UserLanguage]
      onChange(updatedLanguages)
      setNewLanguage({
        language_id: 0,
        language_name: '',
        speaking_rating: 3,
        reading_rating: 3,
        writing_rating: 3
      })
      setShowAddForm(false)
    }
  }

  const handleRemoveLanguage = (languageId: number) => {
    const updatedLanguages = value.filter(lang => lang.language_id !== languageId)
    onChange(updatedLanguages)
  }

  const handleLanguageSelect = (languageId: string) => {
    const selectedLanguage = availableLanguages.find(lang => lang.id === parseInt(languageId))
    if (selectedLanguage) {
      setNewLanguage({
        ...newLanguage,
        language_id: selectedLanguage.id,
        language_name: selectedLanguage.language_name
      })
    }
  }

  const renderStarRating = (rating: number, onChange: (rating: number) => void, disabled: boolean = false) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => !disabled && onChange(star)}
            disabled={disabled}
            className={`${
              star <= rating 
                ? 'text-yellow-400' 
                : 'text-gray-300'
            } hover:text-yellow-400 transition-colors ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <Star className="w-4 h-4 fill-current" />
          </button>
        ))}
        <span className="text-xs text-gray-500 ml-2">
          {RATING_LABELS[rating as keyof typeof RATING_LABELS]}
        </span>
      </div>
    )
  }

  const handleUpdateLanguage = (languageId: number, field: keyof UserLanguage, newValue: any) => {
    const updatedLanguages = value.map(lang => 
      lang.language_id === languageId 
        ? { ...lang, [field]: newValue }
        : lang
    )
    onChange(updatedLanguages)
  }

  if (isLoading && availableLanguages.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">Languages</label>
        </div>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="text-xs text-gray-500">Loading languages...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">Languages</label>
        {!disabled && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700"
          >
            <Plus className="w-4 h-4" />
            <span>Add Language</span>
          </button>
        )}
      </div>

      {/* Current Languages */}
      <div className="space-y-3">
        {value.map((userLang) => (
          <div key={userLang.language_id} className="bg-gray-50 rounded-lg p-4 border">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">{userLang.language_name}</h4>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveLanguage(userLang.language_id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="space-y-4">
              {/* Skills Ratings */}
              <div className="space-y-3">
                <label className="block text-xs font-medium text-gray-500">Rate Your Skills (1-5 stars)</label>
                
                {/* Speaking */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 w-20">Speaking:</span>
                  {renderStarRating(
                    userLang.speaking_rating || 3, 
                    (rating) => handleUpdateLanguage(userLang.language_id, 'speaking_rating', rating),
                    disabled
                  )}
                </div>

                {/* Reading */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 w-20">Reading:</span>
                  {renderStarRating(
                    userLang.reading_rating || 3, 
                    (rating) => handleUpdateLanguage(userLang.language_id, 'reading_rating', rating),
                    disabled
                  )}
                </div>

                {/* Writing */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 w-20">Writing:</span>
                  {renderStarRating(
                    userLang.writing_rating || 3, 
                    (rating) => handleUpdateLanguage(userLang.language_id, 'writing_rating', rating),
                    disabled
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {value.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-4">No languages added yet.</p>
        )}
      </div>

      {/* Add Language Form */}
      {showAddForm && !disabled && (
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <h4 className="font-medium text-gray-900 mb-3">Add New Language</h4>
          
          <div className="space-y-4">
            {/* Language Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Language</label>
              <select
                value={newLanguage.language_id || ''}
                onChange={(e) => handleLanguageSelect(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Choose a language...</option>
                {availableLanguagesForDropdown.length > 0 ? (
                  availableLanguagesForDropdown.map((language) => (
                    <option key={language.id} value={language.id}>
                      {language.language_name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    {isLoading ? 'Loading languages...' : 'No languages available'}
                  </option>
                )}
              </select>
              {/* Debug info */}
              <div className="text-xs text-gray-500 mt-1">
                Debug: {availableLanguages.length} total languages, {availableLanguagesForDropdown.length} available for selection
              </div>
            </div>

            {/* Skills Ratings */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">Rate Your Skills (1-5 stars)</label>
              
              {/* Speaking */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 w-20">Speaking:</span>
                {renderStarRating(
                  newLanguage.speaking_rating || 3, 
                  (rating) => setNewLanguage({ ...newLanguage, speaking_rating: rating })
                )}
              </div>

              {/* Reading */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 w-20">Reading:</span>
                {renderStarRating(
                  newLanguage.reading_rating || 3, 
                  (rating) => setNewLanguage({ ...newLanguage, reading_rating: rating })
                )}
              </div>

              {/* Writing */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 w-20">Writing:</span>
                {renderStarRating(
                  newLanguage.writing_rating || 3, 
                  (rating) => setNewLanguage({ ...newLanguage, writing_rating: rating })
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-2 pt-2">
              <button
                type="button"
                onClick={handleAddLanguage}
                disabled={!newLanguage.language_id}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Language
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false)
                  setNewLanguage({
                    language_id: 0,
                    language_name: '',
                    speaking_rating: 3,
                    reading_rating: 3,
                    writing_rating: 3
                  })
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}